/*
 * Minimal JSON Schema validator for the subset of Draft-07 our tool schemas use.
 *
 * Deliberately hand-written instead of pulling in ajv: ajv compiles schemas with
 * `new Function(...)`, and this project's security rule is that no agent-reachable
 * code path ever constructs code at runtime. The subset below is everything the
 * tool definitions actually need; anything outside it throws at registration time
 * (see assertSupportedSchema) rather than silently passing validation.
 */

const SUPPORTED_KEYWORDS = new Set([
  'type', 'description', 'default', 'enum', 'const',
  'properties', 'required', 'additionalProperties',
  'items', 'minItems', 'maxItems',
  'minimum', 'maximum', 'exclusiveMinimum', 'exclusiveMaximum', 'multipleOf',
  'minLength', 'maxLength', 'pattern',
  'title', 'examples'
])

const TYPES = new Set(['object', 'array', 'string', 'number', 'integer', 'boolean', 'null'])

/** Throws if a schema uses a keyword this validator would silently ignore. */
export function assertSupportedSchema (schema, path = '') {
  if (schema === undefined || schema === null) return
  if (typeof schema !== 'object' || Array.isArray(schema)) {
    throw new Error(`Schema at "${path || '/'}" must be an object`)
  }
  for (const key of Object.keys(schema)) {
    if (!SUPPORTED_KEYWORDS.has(key)) {
      throw new Error(`Unsupported JSON Schema keyword "${key}" at "${path || '/'}" — extend validate.js or simplify the schema`)
    }
  }
  if (schema.type !== undefined && !TYPES.has(schema.type)) {
    throw new Error(`Unsupported "type": ${JSON.stringify(schema.type)} at "${path || '/'}"`)
  }
  if (schema.properties) {
    for (const [k, v] of Object.entries(schema.properties)) assertSupportedSchema(v, `${path}/${k}`)
  }
  if (schema.items) assertSupportedSchema(schema.items, `${path}/items`)
}

function typeOf (value) {
  if (value === null) return 'null'
  if (Array.isArray(value)) return 'array'
  return typeof value
}

function matchesType (value, type) {
  if (type === 'integer') return typeof value === 'number' && Number.isInteger(value)
  if (type === 'number') return typeof value === 'number' && Number.isFinite(value)
  return typeOf(value) === type
}

/**
 * @returns {{valid: boolean, errors: Array<{path: string, message: string}>}}
 */
export function validate (schema, value, path = '') {
  const errors = []
  walk(schema, value, path || '', errors)
  return { valid: errors.length === 0, errors }
}

function walk (schema, value, path, errors) {
  if (!schema || typeof schema !== 'object') return
  const at = path || '(root)'

  if (schema.type !== undefined && !matchesType(value, schema.type)) {
    errors.push({ path: at, message: `expected type "${schema.type}" but got ${typeOf(value)}` })
    return
  }

  if (schema.enum !== undefined && !schema.enum.some((e) => deepEqual(e, value))) {
    errors.push({ path: at, message: `must be one of ${JSON.stringify(schema.enum)}` })
  }
  if (schema.const !== undefined && !deepEqual(schema.const, value)) {
    errors.push({ path: at, message: `must equal ${JSON.stringify(schema.const)}` })
  }

  if (typeof value === 'number') {
    if (schema.minimum !== undefined && value < schema.minimum) {
      errors.push({ path: at, message: `must be >= ${schema.minimum}` })
    }
    if (schema.maximum !== undefined && value > schema.maximum) {
      errors.push({ path: at, message: `must be <= ${schema.maximum}` })
    }
    if (schema.exclusiveMinimum !== undefined && value <= schema.exclusiveMinimum) {
      errors.push({ path: at, message: `must be > ${schema.exclusiveMinimum}` })
    }
    if (schema.exclusiveMaximum !== undefined && value >= schema.exclusiveMaximum) {
      errors.push({ path: at, message: `must be < ${schema.exclusiveMaximum}` })
    }
    if (schema.multipleOf !== undefined && !isMultipleOf(value, schema.multipleOf)) {
      errors.push({ path: at, message: `must be a multiple of ${schema.multipleOf}` })
    }
  }

  if (typeof value === 'string') {
    if (schema.minLength !== undefined && value.length < schema.minLength) {
      errors.push({ path: at, message: `must be at least ${schema.minLength} characters` })
    }
    if (schema.maxLength !== undefined && value.length > schema.maxLength) {
      errors.push({ path: at, message: `must be at most ${schema.maxLength} characters` })
    }
    if (schema.pattern !== undefined && !new RegExp(schema.pattern).test(value)) {
      errors.push({ path: at, message: `must match pattern ${schema.pattern}` })
    }
  }

  if (Array.isArray(value)) {
    if (schema.minItems !== undefined && value.length < schema.minItems) {
      errors.push({ path: at, message: `must have at least ${schema.minItems} items` })
    }
    if (schema.maxItems !== undefined && value.length > schema.maxItems) {
      errors.push({ path: at, message: `must have at most ${schema.maxItems} items` })
    }
    if (schema.items) {
      value.forEach((item, i) => walk(schema.items, item, `${path}[${i}]`, errors))
    }
  }

  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    for (const key of schema.required || []) {
      if (!Object.prototype.hasOwnProperty.call(value, key)) {
        errors.push({ path: path ? `${path}.${key}` : key, message: 'is required' })
      }
    }
    const props = schema.properties || {}
    for (const [key, sub] of Object.entries(props)) {
      if (Object.prototype.hasOwnProperty.call(value, key) && value[key] !== undefined) {
        walk(sub, value[key], path ? `${path}.${key}` : key, errors)
      }
    }
    if (schema.additionalProperties === false) {
      for (const key of Object.keys(value)) {
        if (!Object.prototype.hasOwnProperty.call(props, key)) {
          errors.push({ path: path ? `${path}.${key}` : key, message: `is not a known property (allowed: ${Object.keys(props).join(', ') || 'none'})` })
        }
      }
    }
  }
}

function isMultipleOf (value, divisor) {
  const q = value / divisor
  return Math.abs(q - Math.round(q)) < 1e-9
}

function deepEqual (a, b) {
  if (a === b) return true
  if (typeOf(a) !== typeOf(b)) return false
  if (Array.isArray(a)) return a.length === b.length && a.every((x, i) => deepEqual(x, b[i]))
  if (a && typeof a === 'object') {
    const ka = Object.keys(a); const kb = Object.keys(b)
    return ka.length === kb.length && ka.every((k) => deepEqual(a[k], b[k]))
  }
  return false
}

/** Fill in `default` values declared on top-level properties of an object schema. */
export function applyDefaults (schema, value) {
  if (!schema || schema.type !== 'object' || !schema.properties) return value
  const out = { ...(value || {}) }
  for (const [key, sub] of Object.entries(schema.properties)) {
    if (out[key] === undefined && sub && sub.default !== undefined) out[key] = sub.default
  }
  return out
}
